import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';

export default function ConfirmEmail() {
  const location = useLocation();
  const emailFromState = (location.state as any)?.email || '';
  const [email, setEmail] = useState(emailFromState);
  const [sent, setSent] = useState(!!emailFromState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleResendConfirmation = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
      }
    });

    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      setSent(true);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a1a] via-[#0a0a0a] to-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img
              src="/slidepusher_events.svg"
              alt="Slidepusher Events"
              className="h-20"
            />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            {sent ? 'Check Your Email' : 'Confirm Your Email'}
          </h1>
          <p className="text-zinc-400">
            {sent ? 'We sent you a confirmation email' : 'Resend confirmation email'}
          </p>
        </div>

        <div className="bg-[#1a1a1a] rounded shadow-xl p-8 border border-zinc-800">
          {sent ? (
            <div>
              <div className="flex items-center justify-center w-16 h-16 bg-green-500/10 rounded-full mx-auto mb-4">
                <Mail className="w-8 h-8 text-green-400" />
              </div>
              <p className="text-center text-zinc-300 mb-6">
                We've sent a confirmation email to <strong className="text-white">{email}</strong>.
                Please check your inbox and click the confirmation link to complete your registration.
              </p>
              <div className="bg-primary-500/10 border border-primary-500/50 rounded p-4 mb-6">
                <p className="text-primary-500 text-sm">
                  <strong>Note:</strong> Check your spam folder if you don't see the email within a few minutes.
                </p>
              </div>
              <button
                onClick={() => setSent(false)}
                className="w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-medium py-3 px-4 rounded transition-colors"
              >
                Didn't receive the email? Click to resend
              </button>
            </div>
          ) : (
            <form onSubmit={handleResendConfirmation} className="space-y-6">
              <div className="bg-yellow-500/10 border border-yellow-500/50 rounded p-4 mb-6">
                <p className="text-yellow-400 text-sm">
                  If you signed up but didn't receive a confirmation email, enter your email below to resend it.
                </p>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded p-3 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-zinc-300 mb-2">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary-500 hover:bg-primary-600 text-white font-medium py-3 px-4 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Resend Confirmation Email'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-300"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
